import type { AppState, Field, Plan, Product } from '../types';
import { id, selectionIssues } from './domain';

const clone = <T>(value: T): T => structuredClone(value);

export function fieldsForPlan(state: AppState, plan: Plan | undefined): Field[] {
  if (!plan) return [];
  return plan.snapshot?.fields ?? state.fields.filter((field) => field.estateId === plan.estateId);
}

export function productsForPlan(state: AppState, plan: Plan | undefined): Product[] {
  return plan?.status === 'ready' ? (plan.snapshot?.products ?? state.products) : state.products;
}

export function finalizePlan(state: AppState, plan: Plan): Plan {
  if (plan.status === 'ready' && plan.snapshot) return clone(plan);
  const fields = fieldsForPlan(state, plan);
  const products = productsForPlan(state, plan);
  if (!fields.length) throw new Error('Tilføj mindst én mark før planen færdiggøres.');
  const issues = selectionIssues(fields, plan, products);
  if (issues.length) throw new Error(issues.join(' '));
  const capturedAt = new Date().toISOString();
  const selectedIds = new Set(Object.values(plan.selections));
  return {
    ...clone(plan),
    status: 'ready',
    updatedAt: capturedAt,
    snapshot: {
      fields: clone(fields),
      products: clone(products.filter((product) => selectedIds.has(product.id))),
      capturedAt,
    },
  };
}

export function copySeason(
  state: AppState,
  source: Plan,
  options: { name?: string; year?: number } = {},
): { state: AppState; plan: Plan } {
  const year = options.year ?? source.year + 1;
  if (!Number.isInteger(year) || year < 1900 || year > 2200)
    throw new Error('Sæsonåret skal ligge mellem 1900 og 2200.');
  const capturedAt = new Date().toISOString();
  // A new season may select any current catalog product; historical selected variants retain their source values.
  const historical = productsForPlan(state, source);
  const productIds = new Set(historical.map((product) => product.id));
  const plan: Plan = {
    ...clone(source),
    id: id(),
    name: options.name?.trim() || `${source.name} · ${year}`,
    year,
    status: 'draft',
    updatedAt: capturedAt,
    snapshot: {
      fields: clone(fieldsForPlan(state, source)),
      products: clone([
        ...historical,
        ...state.products.filter((product) => !productIds.has(product.id)),
      ]),
      capturedAt,
    },
  };
  return {
    state: {
      ...state,
      plans: [...state.plans, plan],
      selectedEstateId: plan.estateId,
      selectedPlanId: plan.id,
    },
    plan,
  };
}

// Earlier versions did not capture season history. Capture current data, never claim to recover past values.
export function captureLegacyPlans(state: AppState): AppState {
  if (!state.plans.some((plan) => plan.status === 'ready' && !plan.snapshot)) return state;
  const capturedAt = new Date().toISOString();
  return {
    ...state,
    plans: state.plans.map((plan) =>
      plan.status === 'ready' && !plan.snapshot
        ? {
            ...plan,
            snapshot: {
              legacy: true,
              capturedAt,
              fields: clone(state.fields.filter((field) => field.estateId === plan.estateId)),
              products: clone(
                state.products.filter((product) =>
                  Object.values(plan.selections).includes(product.id),
                ),
              ),
            },
          }
        : plan,
    ),
  };
}
